import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";

import useWindowSize from "../hooks/useWindowSize";
import {
  CustomiserSwitchOption,
  ExpandState,
  FloorplanOption,
  Home,
  IconName,
} from "../types";
import { getPriceFormatted } from "../util";
import CustomiserSwitchList from "./CustomiserSwitchList";
import Fancybox from "./Fancybox";
import FloorplanSVG from "./FloorplanSVG";
import Icon from "./Icon";
import Tabs from "./Tabs";

// @ts-ignore
let timeoutRef = null;
const FloorplanCustomiser = observer(
  (props: {
    readonly?: boolean;
    isHouseLand?: boolean;
    home: Home;
    isActive: boolean;
    isFloorplanFlipped: boolean;
    onFlipFloorplan?: (isFlipped: boolean) => void;
    selectedOptions: FloorplanOption[];
    onClickShowLightbox?: (svg: string) => void;
    onClickOption?: (option: CustomiserSwitchOption) => void;
    // Mobile specific
    onClickMobilePrev?: () => void;
    onClickMobileNext?: () => void;
    floorplanNumber: number;
    totalFloorplans: number;
  }) => {
    const [expandState, setExpandState] =
      useState<ExpandState>("state-expanded");
    const [expandToHeight, setExpandToHeight] = useState(0);

    const [tabIndex, setTabIndex] = useState(0);

    const contentInnerRef = useRef<HTMLDivElement>(null);

    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const floorplanDetailsRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<SimpleBar>(null);
    const [scrollContainerHeight, setScrollContainerHeight] = useState(140); // Value for Mobile
    const [scrollContainerMinHeight, setScrollContainerMinHeight] = useState(0); // Value for Mobile

    const windowSize = useWindowSize();

    useEffect(() => {
      // Need to re-calculate the width of the tab scroll
      if (props.isHouseLand && scrollContainerRef.current) {
        setScrollContainerHeight(10000);
        scrollContainerRef.current.el.style.maxHeight = "100%";
      } else if (
        windowSize.width > 992 &&
        optionsContainerRef.current &&
        floorplanDetailsRef.current &&
        scrollContainerRef.current
      ) {
        scrollContainerRef.current.el.style.maxHeight = "0";
        const newHeight =
          optionsContainerRef.current.offsetHeight -
          floorplanDetailsRef.current.offsetHeight -
          67; // 67 = Height of 'Floorplan Options' title including padding
        setScrollContainerHeight(newHeight);
        scrollContainerRef.current.el.style.maxHeight = newHeight + "px";
        setScrollContainerMinHeight(130);
      } else {
        setScrollContainerHeight(140);
        setScrollContainerMinHeight(0);
      }
    }, [
      props.isHouseLand,
      optionsContainerRef.current,
      scrollContainerRef.current,
      windowSize.width,
    ]);

    useEffect(() => {
      // Default state is open, we need to set the height on load
      if (contentInnerRef.current && expandState === "state-expanded") {
        setExpandToHeight(
          contentInnerRef.current.getBoundingClientRect().height
        );
      }
    }, [contentInnerRef.current]);

    const onClickMobileHeader = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (props.readonly) {
        return;
      }

      if (contentInnerRef.current) {
        // @ts-ignore
        clearTimeout(timeoutRef);

        let targetState: ExpandState = "state-expanded";
        if (
          expandState === "state-expanded" ||
          expandState === "state-expanding"
        ) {
          targetState = "state-collapsed";
        }
        if (targetState === "state-expanded") {
          setExpandToHeight(
            contentInnerRef.current.getBoundingClientRect().height
          );
          setExpandState("state-expanding");
        } else {
          setExpandState("state-collapsing");
          requestAnimationFrame(() => {
            setExpandToHeight(0);
          });
        }

        timeoutRef = setTimeout(function () {
          setExpandState(targetState);
        }, 700);
      }
    };

    const onClickMobilePrev = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (props.onClickMobilePrev) {
        props.onClickMobilePrev();
      }
    };

    const onClickMobileNext = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (props.onClickMobileNext) {
        props.onClickMobileNext();
      }
    };

    const onChangeMobileTabIndex = (index: number) => {
      setTabIndex(index);
    };

    const onClickSwitchOption = (option: CustomiserSwitchOption) => {
      console.log("onClickSwitchOption", props.onClickOption);
      if (props.onClickOption) {
        props.onClickOption(option);

        // If the option is on the second storey, we need to switch to that tab
        if (props.home.storeys > 1) {
          const secondFloorSvg = getMobileFloorplanSvg(1);
          const svgId = (option as FloorplanOption).svg_id;
          const re = new RegExp(`id="\\S*${svgId}"`);
          const result = secondFloorSvg.match(re);
          if (result !== null) {
            setTabIndex(1);
          } else {
            setTabIndex(0);
          }
        }
      }
    };

    const onClickLightbox = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (props.onClickShowLightbox) {
        props.onClickShowLightbox(
          props.isFloorplanFlipped && !props.home.flip_css
            ? props.home.floorplan_combined_flip
            : props.home.floorplan_combined
        );
      }
    };

    const renderActions = () => {
      let canFlip = !!props.home.floorplan_combined_flip || props.home.flip_css;
      let canFullscreen = false;
      if (windowSize.width < 992) {
        canFlip = !!props.home.floorplan_lower_flip || props.home.flip_css;
        canFullscreen = !!props.onClickShowLightbox;
      }
      return (
        <div className={"actions"}>
          {props.home.virtual_tour_url && (
            <div role={"button"} className={"action-virtual-tour"}>
              <Fancybox
                id={`virtual-tour-${props.home.id}`}
                dataType={"iframe"}
                dataSrc={props.home.virtual_tour_url}
              >
                <Icon name={IconName.THREE_D} />
              </Fancybox>
            </div>
          )}
          {canFlip && (
            <div
              role={"button"}
              onClick={() =>
                props.onFlipFloorplan &&
                props.onFlipFloorplan(!props.isFloorplanFlipped)
              }
              className={`action-flip ${
                props.isFloorplanFlipped ? "flipped" : ""
              }`}
            >
              <Icon name={IconName.FLIP} />
            </div>
          )}
          {canFullscreen && (
            <div
              role={"button"}
              onClick={onClickLightbox}
              className={"action-fullscreen"}
            >
              <Icon name={IconName.FULLSCREEN} />
            </div>
          )}
        </div>
      );
    };
    const renderFloorplanDetails = () => {
      return (
        <div className={"details"}>
          <div className={"details-row"}>
            <span className={"label"}>Size</span>
            <span className={"value"}>
              {props.home.total_area_m}m&sup2; ({props.home.total_area_sq}
              sq)
            </span>
          </div>
          <div className={"details-row"}>
            <span className={"label"}>Width</span>
            <span className={"value"}>{props.home.width}m</span>
          </div>
          <div className={"details-row"}>
            <span className={"label"}>Length</span>
            <span className={"value"}>{props.home.length}m</span>
          </div>
          <div className={"icons-row"}>
            <div className={"icons-item"}>
              <Icon name={IconName.BED} />
              <span>{props.home.bedrooms}</span>
            </div>
            <div className={"icons-item"}>
              <Icon name={IconName.COUCH} />
              <span>{props.home.living_areas}</span>
            </div>
            <div className={"icons-item"}>
              <Icon name={IconName.BATH} />
              <span>{props.home.bathrooms}</span>
            </div>
            <div className={"icons-item"}>
              <Icon name={IconName.CAR} />
              <span>{props.home.car_spaces}</span>
            </div>
          </div>
        </div>
      );
    };

    const renderFloorplanOptionsReadonly = () => {
      return props.selectedOptions.length > 0 ? (
        <div className={"floorplan-options-list readonly"}>
          {props.selectedOptions.map((item) => {
            return (
              <div key={item.id} className={"floorplan-option-readonly"}>
                <Icon name={IconName.CHECK_MARK_CIRCLE} />
                <span className={"label"}>{item.label}</span>
                <span className={"price"}>{getPriceFormatted(item.price)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={"floorplan-options-empty"}>
          No floorplan options selected
        </p>
      );
    };

    const getMobileFloorplanSvg = (tabIndex: number): string => {
      if (tabIndex === 0) {
        return props.isFloorplanFlipped && !props.home.flip_css
          ? props.home.floorplan_lower_flip
          : props.home.floorplan_lower;
      } else {
        return props.isFloorplanFlipped && !props.home.flip_css
          ? props.home.floorplan_upper_flip
          : props.home.floorplan_upper;
      }
    };

    return (
      <div
        className={`pyht-floorplan-customiser floorplan-${props.home.house_type}`}
        id={`pyht-floorplan-customer-${props.home.id}`}
      >
        <div className={"floorplan-details"}>
          <div className={"layout-mobile"}>
            {!props.readonly && !props.isHouseLand && (
              <div className={"carousel-navbar"}>
                <div
                  role={"button"}
                  className={"carousel-navbar-control prev"}
                  onClick={onClickMobilePrev}
                >
                  <Icon name={IconName.ARROW} />
                </div>
                <div className="carousel-navbar-instructions">Swipe to browse floorplans</div>
                <div className="carousel-navbar-count">
                  {props.floorplanNumber.toString(10).padStart(2, "0")}{" "}
                  / {props.totalFloorplans.toString(10).padStart(2, "0")}
                </div>
                <div
                  role={"button"}
                  className={"carousel-navbar-control next"}
                  onClick={onClickMobileNext}
                >
                  <Icon name={IconName.ARROW} />
                </div>
              </div>
            )}
            {!props.isHouseLand && (
              <>
                <div
                  className={`header ${expandState}`}
                  role={"button"}
                  onClick={onClickMobileHeader}
                >
                  <div className={"title"}>
                    <h3>{props.home.title}</h3>
                    {props.home.price && (
                      <p>From ${props.home.price.toLocaleString()}</p>
                    )}
                  </div>

                  {!props.readonly && (
                    <Icon
                      name={IconName.CHEVRON_DOWN}
                      className={`direction-icon ${expandState}`}
                    />
                  )}
                </div>
                <div
                  className={`expandable-content ${expandState}`}
                  style={{ height: `${expandToHeight}px` }}
                >
                  <div ref={contentInnerRef}>{renderFloorplanDetails()}</div>
                </div>
              </>
            )}

            {props.home.storeys > 1 ? (
              <Tabs
                titles={["Lower Level", "Upper Level"]}
                tabIndex={tabIndex}
                onChangeTabIndex={onChangeMobileTabIndex}
                renderContent={(index) => (
                  <div className={"floorplan-viewer"} onClick={onClickLightbox}>
                    <div className={"inner"}>
                      <FloorplanSVG
                        vpbPkid={props.home.vpb_pkid}
                        layerIds={props.home.floorplan_options.map(
                          (option) => option.svg_id
                        )}
                        visibleLayers={props.selectedOptions.map(
                          (option) => option.svg_id
                        )}
                        svg={getMobileFloorplanSvg(index)}
                        flipCss={props.home.flip_css && props.isFloorplanFlipped}
                      />
                    </div>
                  </div>
                )}
              />
            ) : (
              <div className={"floorplan-viewer"} onClick={onClickLightbox}>
                <div className={"inner"}>
                  <FloorplanSVG
                    vpbPkid={props.home.vpb_pkid}
                    layerIds={props.home.floorplan_options.map(
                      (option) => option.svg_id
                    )}
                    visibleLayers={props.selectedOptions.map(
                      (option) => option.svg_id
                    )}
                    svg={
                      props.isFloorplanFlipped && !props.home.flip_css
                        ? props.home.floorplan_combined_flip
                        : props.home.floorplan_combined
                    }
                    flipCss={props.home.flip_css && props.isFloorplanFlipped}
                  />
                </div>
              </div>
            )}
            {props.home.floorplan_options.length > 0 && (
              <div className={"floorplan-options"}>
                <div className={"floorplan-options-header"}>
                  <p className={"section-label"}>Floorplan Options</p>
                  {!props.readonly && !props.isHouseLand && renderActions()}
                </div>
                {props.readonly ? (
                  renderFloorplanOptionsReadonly()
                ) : (
                  <div className={"floorplan-options-list"}>
                    <CustomiserSwitchList
                      items={props.home.floorplan_options}
                      selected={props.selectedOptions}
                      onClickOption={onClickSwitchOption}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={"layout-desktop"}>
          <div
            className={`floorplan-viewer ${
              props.home.floorplan_options.length === 0 ? "single-col" : ""
            }`}
          >
            <div className={"inner"}>
              {!props.readonly && props.isActive && (
                <div
                  className={"fullscreen-btn"}
                  role={"button"}
                  onClick={onClickLightbox}
                >
                  <Icon name={IconName.FULLSCREEN} />
                </div>
              )}
              <FloorplanSVG
                vpbPkid={props.home.vpb_pkid}
                layerIds={props.home.floorplan_options.map(
                  (option) => option.svg_id
                )}
                visibleLayers={props.selectedOptions.map(
                  (option) => option.svg_id
                )}
                svg={
                  props.isFloorplanFlipped && !props.home.flip_css
                    ? props.home.floorplan_combined_flip
                    : props.home.floorplan_combined
                }
                flipCss={props.home.flip_css && props.isFloorplanFlipped}
              />
            </div>
          </div>
          {props.home.floorplan_options.length > 0 && (
            <div
              className={`floorplan-options-container ${
                props.readonly ? "readonly" : ""
              }`}
              ref={optionsContainerRef}
            >
              {!props.isHouseLand && (
                <div className={"floorplan-details"} ref={floorplanDetailsRef}>
                  <div className={"header"}>
                    <div className={"title"}>
                      <h3>{props.home.title}</h3>
                      {props.home.price && (
                        <p>From ${props.home.price.toLocaleString()}</p>
                      )}
                    </div>
                    {!props.readonly && renderActions()}
                  </div>
                  {renderFloorplanDetails()}
                </div>
              )}
              <div
                className={`floorplan-options ${
                  props.readonly ? "readonly" : ""
                }`}
              >
                <p className={"section-label"}>
                  Floorplan Options{props.readonly && " Included"}
                </p>

                {props.readonly ? (
                  renderFloorplanOptionsReadonly()
                ) : (
                  <SimpleBar
                    autoHide={false}
                    ref={scrollContainerRef}
                    style={{ maxHeight: scrollContainerHeight, minHeight: scrollContainerMinHeight }}
                  >
                    <div className={"scrollbars-inner"}>
                      <div className={"floorplan-options-list"}>
                        <CustomiserSwitchList
                          items={props.home.floorplan_options}
                          selected={props.selectedOptions}
                          onClickOption={onClickSwitchOption}
                        />
                      </div>
                    </div>
                  </SimpleBar>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default FloorplanCustomiser;
